import React, { useEffect, useState, useRef } from 'react';
import '../styles/Profile.css';
import { Header, Footer, Button, DefaultUserImage, Loading, Modal } from '../components';
import { checkOnAuthStateChanged, cleanPhoneNumber, formatPhoneNumber, getDocument, logout, recordEvent, setTransaction, uploadImage } from '../functions';
import { PhoneNumberInput } from '../modalbody';
import { useNavigate } from 'react-router-dom';

function Profile() {
    const [loading, setLoading] = useState(true);
    const [userId, setUserId] = useState(null);
    const [loggedIn, setLoggedIn] = useState(null);
    const [userData, setUserData] = useState(null);
    const [companyData, setCompanyData] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [newPhoneNumber, setNewPhoneNumber] = useState("");
    const [editLoading, setEditLoading] = useState(false);
    const [imageLoading, setImageLoading] = useState(null);
    const [companyId, setCompanyId] = useState(null);
    const [imageError, setImageError] = useState(null);
    const fileInputRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        document.title = "Profile | Clozers";

        const fetchCompanyData = async(newCompanyId) => {
            const colRef = "companies";
            const queryData = await getDocument(colRef, newCompanyId);
            if ( queryData.status === 200 ) {
                setCompanyData(queryData.data.data);
                setCompanyId(newCompanyId);
            }
        };

        const fetchUserData = async() => {
            const user = await checkOnAuthStateChanged();
            if ( user.status === 200 ) {
                setLoggedIn(true);
                setUserId(user.userId);

                const colRef = "users";
                const docRef = user.userId;
                const queryData = await getDocument(colRef, docRef);
                if ( queryData.status === 200 ) {
                    setUserData(queryData.data.data);
                    fetchCompanyData(queryData.data.data.companyId);

                    if (queryData?.data?.data?.phoneNumber) {
                        setNewPhoneNumber(formatPhoneNumber(queryData.data.data.phoneNumber));
                    }
                }
                else {
                    setLoggedIn(false);
                    navigate("/");
                }
            }
            else {
                setLoggedIn(false);
                navigate("/");
            }
            setLoading(false);
        };

        fetchUserData();
    }, [navigate]);

    const elements = [
        {
            label: "Company",
            subLabel: null,
            showImage: true,
            image: companyData ? companyData.logo : null,
            value: companyData ? companyData.companyName : "",
            logout: false,
            editable: false
        },
        {
            label: "Name",
            subLabel: null,
            showImage: false,
            image: null,
            value: userData ? `${userData.firstName} ${userData.lastName}` : "",
            logout: false,
            editable: false
        },
        {
            label: "Email",
            subLabel: null,
            showImage: false,
            image: null,
            value: userData ? userData.email : "",
            logout: false,
            editable: false
        },
        {
            label: "Phone number",
            subLabel: "Link your phone number to use text to ARV feature.",
            showImage: false,
            image: null,
            value: userData?.phoneNumber && userData.phoneNumber !== "" ? `+1 ${formatPhoneNumber(userData.phoneNumber)}` : "",
            logout: false,
            editable: true
        },
        {
            label: null,
            subLabel: null,
            showImage: false,
            image: null,
            value: null,
            logout: true,
            editable: false
        }
    ];

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            chooseLogo(e.target.files[0]);
        }
    };

    const openFilePicker = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

const chooseLogo = async(file) => {
    setImageError(null);
    const ext = file.name.includes('.') ? file.name.split('.').pop() : 'jpg';
    const storagePath = `logos/${companyId}/logo.${ext}`;
    const getURL = await uploadImage(file, storagePath, setImageLoading);
    if ( getURL.status === 200 ) {
        await setTransaction("companies", companyId, "logo", getURL.url, null);
        const cloneData = {...companyData};
        cloneData.logo = getURL.url;
        setCompanyData(cloneData);
        setImageLoading(null);
        recordEvent("logo_changed", {
            logo: getURL.url
        });
    }
    else {
        setImageLoading(null);
        recordEvent("logo_change_failed", {
            error: getURL.message
        });
        setImageError(getURL.message);
    }
};

    const openPhoneModal = () => {
        setShowModal(true);
    };

    const closePhoneModal = () => {
        setShowModal(false);
        setNewPhoneNumber(userData?.phoneNumber ? formatPhoneNumber(userData.phoneNumber) : "");
        recordEvent("phone_number_modal_closed", {});
    };

    const onChangePhoneNumber = async(value) => {
        const amendedVal = await formatPhoneNumber(value);
        setNewPhoneNumber(amendedVal);
    };

    const savePhoneNumber = async() => {
        setEditLoading(true);
        const cleanedNumber = await cleanPhoneNumber(newPhoneNumber);
        const finalPhoneNumber = `+1${cleanedNumber}`;
        await setTransaction("users", userId, "phoneNumber", finalPhoneNumber, null);
        const cloneData = {...userData};
        cloneData.phoneNumber = finalPhoneNumber;
        setUserData(cloneData);
        setShowModal(false);
        recordEvent("phone_number_added", {
            phoneNumber: finalPhoneNumber
        });
        setEditLoading(false);
    };

    const logoutUser = async() => {
        await logout();
        navigate("/");
    };

    return (
        <div className="profile-outer-container">
            <Header
                loggedIn={loggedIn}
            />
            <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: 'none' }}
            />
            {
                showModal === true &&
                <div className="dark-bg">
                </div>
            }
            <div className="profile-inner-container relative-container">
                {
                    showModal === true &&
                    <Modal
                        heading="Add or link phone number"
                        subheading={"Enter your phone number to access your text-to-ARV credits. Already tried it? Use the same number to link your account."}
                        body={<PhoneNumberInput phoneNumber={newPhoneNumber} onChangePhoneNumber={onChangePhoneNumber} />}
                        type="basic"
                        cancelText={"Cancel"}
                        cancelButtonCTA={closePhoneModal}
                        actionButtonText={"Save"}
                        actionButtonCTA={savePhoneNumber}
                        secondaryButtonText={null}
                        secondaryButtonCTA={null}
                        exitButtonCTA={closePhoneModal}
                        loading={editLoading}
                        actionButtonDisabled={editLoading === true ? true : cleanPhoneNumber(newPhoneNumber).length !== 10 ? true : false}
                        leftIcon={null}
                        rightIcon={null}
                        cancelLeftIcon={null}
                        cancelRightIcon={null}
                        customClass="phone-number-input-modal"
                    />
                }
                {
                    loading === true &&
                    <Loading />
                }
                <div className="profile-title-container">
                    <h1 className="heading-large-bold colour-primary">
                        Profile
                    </h1>
                </div>
                <div className="profile-border-container margin-tops">
                </div>
                <div className="profile-body-container">
                    {
                        elements.map((item, index) =>
                            item.logout ?
                            <div 
                                className="profile-element-container"
                                key={index}
                            >
                                <Button
                                    buttonType="secondary-button"
                                    size="large"
                                    text={"Logout"}
                                    cta={() => logoutUser()}
                                    disabled={loading}
                                    leftIcon={null}
                                    leftIconClass=""
                                    rightIcon={null}
                                    rightIconClass=""
                                    alt=""
                                    standardIcon={false}
                                    badge={false}
                                    badgeVal={null}
                                    badgeBG={null}
                                    buttonRef={null}
                                    spinner={false}
                                />
                            </div>
                            :
                            <React.Fragment
                                key={index}
                            >
                                <div className="profile-element-row">
                                    <div className="profile-element-container">
                                        <span className="body-bold colour-primary">
                                            {item.label}
                                        </span>
                                        {
                                            item.subLabel && item.value === "" &&
                                            <span className="body-regular colour-secondary">
                                                {item.subLabel}
                                            </span>
                                        }
                                        {
                                            item.showImage && imageLoading === null ?
                                            <DefaultUserImage
                                                size="large"
                                                clickFunc={() => openFilePicker()}
                                                image={item.image}
                                            />
                                            :
                                            item.showImage && imageLoading !== null ?
                                            <div className="profile-image-loading-container">
                                                <span className="body-bold colour-primary">
                                                    {imageLoading}
                                                </span>
                                            </div>
                                            :
                                            null
                                        }
                                        {
                                            item.showImage === true && imageError !== null &&
                                            <span className="body-regular colour-error">
                                                {imageError}
                                            </span>
                                        }
                                        {
                                            item.label === "Phone number" && item.value === "" && loading === false ?
                                            <Button
                                                buttonType="primary-button"
                                                size="large"
                                                text={"Add or link phone number"}
                                                cta={() => openPhoneModal()}
                                                disabled={loading}
                                                leftIcon={null}
                                                leftIconClass=""
                                                rightIcon={null}
                                                rightIconClass=""
                                                alt=""
                                                standardIcon={false}
                                                badge={false}
                                                badgeVal={null}
                                                badgeBG={null}
                                                buttonRef={null}
                                                spinner={false}
                                            />
                                            :
                                            <span className="body-regular colour-primary">
                                                {item.value}
                                            </span>
                                        }
                                    </div>
                                    {
                                        item.editable &&
                                        <div className="profile-element-edit-container">
                                            <span 
                                                className="body-regular colour-primary underline cursor-pointer"
                                                onClick={() => openPhoneModal()}
                                            >
                                                Edit
                                            </span>
                                        </div>
                                    }
                                </div>
                                {
                                    index !== elements.length - 1 &&
                                    <div className="profile-border-container margin-tops">
                                    </div>
                                }
                            </React.Fragment>
                        )
                    }
                </div>
                <div className="">
                    
                </div>
            </div>
            <Footer />
        </div>
    )
};

export default Profile;